import { Box, Typography, Button, Dialog, DialogContent, Grid } from '@mui/material'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import QRCode from 'react-qr-code'
import { i18nTranslationHelper } from '@/i18n'
import { useEffect, useRef } from 'react'

interface WhatsAppModalProps {
  open: boolean
  onClose: () => void
  phoneNumber?: string
  companyName?: string
}

const WhatsAppModal = ({ open, onClose, phoneNumber = '' }: WhatsAppModalProps) => {
  const t = i18nTranslationHelper.getTComponents

  const message = t('whatsappModal.defaultMessage')
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`

  const handleWhatsAppClick = () => {
    window.open(whatsappUrl, '_blank')
    onClose()
  }

  const openWhatsAppButtonRef = useRef<HTMLButtonElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Gerenciar foco para acessibilidade
  useEffect(() => {
    if (open) {
      // Salvar elemento com foco anterior
      previousFocusRef.current = document.activeElement as HTMLElement

      // Focar no botão principal quando abrir
      setTimeout(() => {
        openWhatsAppButtonRef.current?.focus()
      }, 100)
    } else if (previousFocusRef.current) {
      // Restaurar foco quando fechar
      previousFocusRef.current.focus()
    }
  }, [open])

  return (
    <Dialog
      id="whatsapp-modal"
      open={open}
      onClose={onClose}
      aria-labelledby="whatsapp-modal-title"
      aria-describedby="whatsapp-modal-description"
    >
      <DialogContent>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          className="text-center"
          gap={2}
        >
          <Box
            sx={{
              backgroundColor: '#E8F5E9',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            role="img"
            aria-label={t('whatsappModal.iconAriaLabel')}
          >
            <WhatsAppIcon sx={{ fontSize: 32, color: '#25D366' }} aria-hidden="true" />
          </Box>

          <Typography id="whatsapp-modal-title" variant="h6" fontWeight="bold" component="h2">
            {t('whatsappModal.title')}
          </Typography>

          <Typography id="whatsapp-modal-description" variant="body1" color="text.secondary">
            {t('whatsappModal.description')}
          </Typography>

          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              padding: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            role="img"
            aria-label={t('whatsappModal.qrcodeAriaLabel', { phoneNumber })}
          >
            <QRCode value={whatsappUrl} size={200} level="M" />
          </Box>

          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              width: '100%'
            }}
            role="region"
            aria-label={t('whatsappModal.contactInfo')}
          >
            <Typography variant="body2" fontWeight={700} gutterBottom id="whatsapp-contact-info">
              {t('whatsappModal.contactInfo')}
            </Typography>
            <Typography variant="body2" aria-labelledby="whatsapp-contact-info">
              {t('whatsappModal.phoneLabel')}: {phoneNumber}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" role="note">
            {t('whatsappModal.note')}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
            width="100%"
            mt={2}
            role="group"
            aria-label={t('whatsappModal.actionsAriaLabel')}
          >
            <Button
              variant="text"
              fullWidth
              sx={{ color: 'grey.500' }}
              onClick={onClose}
              aria-label={t('whatsappModal.cancelButtonAriaLabel')}
            >
              {t('whatsappModal.cancelButton')}
            </Button>

            <Button
              ref={openWhatsAppButtonRef}
              variant="contained"
              fullWidth
              startIcon={<WhatsAppIcon aria-hidden="true" />}
              onClick={handleWhatsAppClick}
              aria-label={t('whatsappModal.openWhatsappButtonAriaLabel', { phoneNumber })}
              sx={{
                backgroundColor: '#25D366',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#20b858'
                }
              }}
            >
              {t('whatsappModal.openWhatsappButton')}
            </Button>
          </Box>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default WhatsAppModal
